import React, { useRef, useState, forwardRef } from "react";
import { IconButton, Link, makeStyles, TableCell, TableRow } from "@material-ui/core";
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  OpenInNew,
  PlaylistAddCheck,
  SaveAlt
} from "@material-ui/icons";
import RefreshIcon from "@material-ui/icons/Refresh";
import locale from "@bonliva-crm/locale";
import {
  CustomerContractSnapshotListItemFragment,
  ConsultantContract_getContractsByConsultantId,
  usePublishCustomerContractSnapshotMutation
} from "@graphql";
import { getObservableQuery } from "@graphql/getObservableQuery";
import { PrimaryButton } from "@components/buttons/PrimaryButton";
import { store } from "@store";
import { SnapshotRowMenu } from "./SnapshotRowMenu";
import { ContractUploaderDialog } from "@consultants/ConsultantContract/ContractUploaderDialog";
import { openBlank } from "@clientUtils";

type Props = {
  data: CustomerContractSnapshotListItemFragment;
  onDuplicate: () => void;
  onGenerate: () => void;
  onDownload: () => void;
  onUpload: (ref) => void;
  onEdit: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onShowInOneflow: () => void;
  onNullify: (value: boolean) => void;
  disabled: boolean;
  open?: boolean;
  onCollapse?: () => void;
  loadingGenerate?: boolean;
};

export const SnapshotRow = forwardRef<Record<string, never>, Props>((props, ref) => {
  const { data } = props;
  const classes = useStyles({});
  const rowRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [publishing, setPublishing] = React.useState(false);

  const runMenuAction = (action: () => void) => {
    action();
    setMenuOpen(false);
  };

  const [publishMutation] = usePublishCustomerContractSnapshotMutation();

  const publishContract = () => {
    setPublishing(true);

    publishMutation({
      variables: {
        snapshotId: data._id
      }
    })
      .then(({ data }) => {
        openBlank(data.publishCustomerContractSnapshot);
        store().snackbars.success("Klart");

        // done
        getObservableQuery(
          ConsultantContract_getContractsByConsultantId.OperationName
        )?.refetch();
      })
      .catch((err) => {
        store().snackbars.error(`Cannot publish contract: ${err.toString()}`);
      })
      .finally(() => {
        setPublishing(false);
      });
  };

  return (
    <TableRow ref={rowRef} className={classes.row}>
      <TableCell component="th" scope="row">
        {props.onCollapse && (
          <IconButton aria-label="expand row" size="small" onClick={props.onCollapse}>
            {props.open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        )}
      </TableCell>

      <TableCell>{data.name}</TableCell>

      <TableCell>
        <div className={classes.iconWrap}>
          {!!data.signedOn && !data.isNullified && (
            <PlaylistAddCheck color={"secondary"} />
          )}
          {data.isNullified && <Close color="primary" />}
          {!data.signedOn && (
            <PrimaryButton
              disabled={props.loadingGenerate}
              loading={props.loadingGenerate}
              leftIcon={<RefreshIcon />}
              onClick={() => props.onGenerate()}
            >
              {locale.Generate}
            </PrimaryButton>
          )}
        </div>
      </TableCell>

      <TableCell>
        <div className={classes.actions}>
          {data.fileUrl && (
            <IconButton onClick={() => props.onDownload()} size="small">
              <SaveAlt />
            </IconButton>
          )}

          {data.viewUrl && (
            <IconButton size="small">
              <Link href={data.viewUrl} target="_blank" className={classes.link}>
                <OpenInNew />
              </Link>
            </IconButton>
          )}

          <IconButton
            key={`menu-${data._id}`}
            ref={menuRef}
            aria-controls="menu"
            aria-haspopup="true"
            size="small"
            onClick={() => setMenuOpen(true)}
            disabled={props.disabled}
          >
            <MoreVert />
          </IconButton>

          <SnapshotRowMenu
            open={menuOpen}
            anchor={menuRef.current}
            onClose={() => setMenuOpen(false)}
            onEdit={() => runMenuAction(props.onEdit)}
            onUpload={() => runMenuAction(() => props.onUpload(ref))}
            onDuplicate={() => runMenuAction(props.onDuplicate)}
            onPublish={() => runMenuAction(publishContract)}
            onOpenInOneflow={() => runMenuAction(props.onShowInOneflow)}
            onNullify={(value) => runMenuAction(() => props.onNullify(value))}
            onDelete={() => runMenuAction(props.onDelete)}
            canEdit={!data.signedOn}
            canOpenInOneflow={!!data.externalId}
            canUpload={!data.signedOn}
            canUnNullify={data.signedOn && data.isNullified}
            canNullify={data.signedOn && !data.isNullified}
            canPublish={true}
          />
        </div>

        <ContractUploaderDialog open={publishing} />
      </TableCell>
    </TableRow>
  );
});

const useStyles = makeStyles(() => ({
  iconWrap: {
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginLeft: 5
    }
  },
  link: {
    display: "inherit"
  },
  actions: {
    minWidth: "90px",
    textAlign: "right"
  },
  dates: {
    maxHeight: 75,
    whiteSpace: "nowrap",
    overflow: "hidden"
  },
  row: {
    "&:last-child > td, &:last-child th": {
      borderBottom: "none"
    }
  }
}));
